import {v4 as uuidv4} from 'uuid';
// import qs from 'query-string';
import {resolve} from './resolve';
import {HyperthymesiaOptions, Device, Level, Payload} from './types';
import HyperthymesiaInstance from './interfaces';
import {
    parseCookie,
    parsePathname,
    parsePerformance,
    parseUserAgent,
    parseQueryString,
    qsStringify,
    parseReferrer,
    parsePath,
    parseFrom
} from './parsers';
import {genRandomInt, throttle} from './utils';
import {SystemLids} from './lids';

class Hyperthymesia implements HyperthymesiaInstance {
    /**
     * Instance
     */
    static instance: Hyperthymesia;
    private startTime: number;
    private pageStartTime: number;
    private hasOperation: boolean = false;
    private _location: any = {};
    private targetUrl: string;
    private targetUrl4Warning?: string;
    private targetUrl4Failed?: string;
    private sendfunc?: Function;
    private _frm?: string;
    protected globalInfo?: any;
    // private readonly canuseSendBeacon: boolean = !!navigator?.sendBeacon;
    private safeGenerate = throttle(this.generate.bind(this), 100);

    public static getInstance(opts: HyperthymesiaOptions): Hyperthymesia {
        if (!this.instance) {
            this.instance = new this(opts);
        }
        return this.instance;
    }

    constructor(opts: HyperthymesiaOptions) {
        const _location = window?.location || opts.defaultEnv?.location || {};
        const _navigator =
            window?.navigator || opts.defaultEnv?.navigator || {};
        const _document = window?.document || opts.defaultEnv?.document || {};
        const _performance =
            window?.performance || opts.defaultEnv?.performance || {};

        if (opts.sendfunc) {
            this.sendfunc = opts.sendfunc;
        }

        this.startTime = Date.now();
        this.pageStartTime = this.startTime;
        this.hasOperation = false;

        const cookie = _document.cookie || '';
        // @ts-ignore
        const {
            userAgent = '',
            language = '',
            // @ts-ignore
            userAgentData = {},
        } = _navigator || '';
        const {search: query, pathname} = _location;

        let initialPerformance = _performance['getEntriesByType']
            ? _performance?.getEntriesByType('navigation')[0] || null
            : null;

        this.targetUrl = resolve(opts.service, opts.target);
        this.targetUrl4Warning = resolve(opts.service, opts.target4Warning || opts.target);
        this.targetUrl4Failed = resolve(opts.service, opts.target4Failed || opts.target);

        const cookieObj = parseCookie(cookie);
        const uaObj = parseUserAgent(userAgent);
        const performanceInfo = parsePerformance(initialPerformance);
        const queryObj = parseQueryString(query) || {};

        this._location = _location;

        let deviceInfo: Device = {
            sw: 0,
            sh: 0,
            dpr: 0,
            ...uaObj,
            lan: language,
            uad: userAgentData,
        };

        if (window) {
            try {
                deviceInfo = Object.assign(deviceInfo, {
                    sw: window.screen?.width,
                    sh: window.screen?.height,
                    svw: window.innerWidth,
                    svh: window.innerHeight,
                    dpr: window.devicePixelRatio || 1
                });
            } catch (err) {
                console.warn(err);
            }
        } else {
            deviceInfo = Object.assign(deviceInfo, opts.deviceInfo);
        }

        let rid = queryObj.rid;
        if (rid instanceof Array) {
            rid = rid[0];
        }

        const globalInfo: any = {
            rid: rid || Date.now().toString() + genRandomInt(100000, 999999),
            pid: opts.pid,
            huid: opts.huid,
            args: opts.defaultArgs,
        };


        if (opts.huid && window?.localStorage) {
            let u = window?.localStorage?.getItem('__huid') || '';

            if (!u) {
                u = uuidv4();
                window.localStorage.setItem('__huid', u);
            }

            globalInfo.huid = u;
        }

        const {cookieKeys, queryKeys, pathnameKeys} = opts;

        if (cookieKeys) {
            cookieKeys.forEach(
                ckey =>
                    (globalInfo[`c_${ckey.toLowerCase()}`] =
                        cookieObj[ckey.toLowerCase()])
            );
        }

        if (queryKeys) {
            queryKeys.forEach(qkey => {
                qkey = qkey.toLowerCase();
                globalInfo[`q_${qkey}`] = queryObj[qkey];
            });
        }

        if (pathnameKeys) {
            pathnameKeys.fields.forEach(
                field =>
                    (globalInfo[`p_${field.toLowerCase()}`] = parsePathname(
                        pathnameKeys.schema,
                        pathname
                    )[field])
            );
        }
        const {referrer, referrerPath} = parseReferrer(_document.referrer || '');
        const frm = parseFrom(_location);
        const track = parseFrom(_location, 'track');
        const initialInfo: {
            _di: Device;
            _pi: any;
            _r: string;
            _rp: string;
            _h: string;
            _frm?: string;
            _track?: string;
        } = {
            _di: deviceInfo,
            _pi: performanceInfo,
            _r: referrer,
            _rp: referrerPath,
            _h: '',
        };

        if (this._location.href) {
            initialInfo._h = this._location.href;
        }

        if(frm) {
            initialInfo['_frm'] = frm;
        }

        if (track) {
            initialInfo['_track'] = track;
        }

        this._frm = frm;
        this.globalInfo = globalInfo;

        if (opts.lazyInit) {
            opts.lazyInit(initialInfo);
        }
        else {
            // 10000000： initialization id
            this.log(SystemLids.initalLog, initialInfo);
            // this.generateBeacon();
            this.addListenPageChange();
        }
    }

    huid() {
        return this.globalInfo?.huid;
    }

    public setHuid(huid: string) {
        if (this.globalInfo) {
            this.globalInfo.huid = huid;
        }
    }

    safeLog(id: number, data?: any): void {
        this.safeGenerate(id, data, Level.Log);
    }

    log(id: number, data?: any): void {
        this.generate(id, data, Level.Log);
    }

    warn(id: number, data?: any): void {
        this.generate(id, data, Level.Warn);
    }

    error(id: number, data?: any): void {
        this.generate(id, data, Level.Error);
    }

    generateUrl(lid: number, data: any = null, level: Level = Level.Log) {
        let target;
        if (level === Level.Warn) {
            target = this.targetUrl4Warning;
        } else if (level === Level.Error) {
            target = this.targetUrl4Failed;
        } else {
            target = this.targetUrl;
        }

        const {pid, rid, huid, ...args} = this.globalInfo;

        const payload: Payload = {
            lv: level,
            lid,
            rid,
            pid,
            u: huid,
            d: Date.now(),
            o: JSON.stringify(args), // 初始化时传递的defaultArgs参数
        };

        if (data) {
            if (this._frm) {
                payload.p = JSON.stringify({_frm: this._frm, ...data});
            } else {
                payload.p = JSON.stringify(data);
            }
        } else if (this._frm) {
            payload.p = JSON.stringify({_frm: this._frm});
        }

        return `${target}?${qsStringify(payload)}`;
    }

    generate(lid: number, data: any = null, level: Level = Level.Log) {
        const url = this.generateUrl(lid, data, level);
        try {
            if (this.sendfunc) {
                this.sendfunc(url);
            } else {
                this.send(url);
            }
        } catch (err) {
            // log url
            throw url;
        }
    }
    // 关闭页面时触发
    generateBeacon() {
        const url = this.generateUrl(SystemLids.endLog, {
            _d: Date.now() - this.startTime, _dp: parsePath(this._location)
        });
        this.startTime = Date.now();
        navigator.sendBeacon(url);
    }

    sendPageEndLog() {
        const time = Date.now();
        // 防止重复发送
        const duration = time - this.pageStartTime;
        if (duration < 100) {
            return;
        }
        // 页面跳转lid 100001
        this.log(SystemLids.pageEndLog, {_d: duration, _dp: parsePath(this._location)});
        this.pageStartTime = time;
    }

    sendEndLog() {
        const time = Date.now();
        // 防止重复发送
        const duration = time - this.startTime;
        // 关闭或页面失焦lid 100003
        this.log(SystemLids.endLog, {_d: duration, _dp: parsePath(this._location)});
        this.startTime = time;
    }

    private send(url: string): void {
        /* 暂时不用 canuseSendBeacon
        if (this.canuseSendBeacon) {
            navigator.sendBeacon(url);
        } else {
            const img = new Image();
            const timer = setTimeout(() => {
                img.src = url;
            }, 0);
            img.onload = img.onerror = img.onabort = () => {
                if (timer) {
                    clearTimeout(timer);
                }
            };
        }
         */

        const img = new Image();
        const timer = setTimeout(() => {
            img.src = url;
        }, 0);
        img.onload = img.onerror = img.onabort = () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }

    rewriteHis(type: 'pushState' | 'replaceState'){
        let origin = window.history[type];
        return function(){
            //@ts-ignore
            let rs = origin.apply(this, arguments);
            let e = new Event(type.toLocaleLowerCase());
            //@ts-ignore
            e.arguments = arguments;
            window.dispatchEvent(e);
            return rs;
        }
    }

    private sendOperationLog() {
        if (!this.hasOperation) {
            this.hasOperation = true;
            // 用户有操作时发送请求，和页面失焦或关闭时的数据相互验证
            this.log(SystemLids.operationLog, {_ho: true, _dp: parsePath(this._location)});
            window.removeEventListener('scroll', this.sendOperationLog);
            window.removeEventListener('click', this.sendOperationLog);
        }
    }

    addListenPageChange() {
        if (window) {
            window.addEventListener('scroll', this.sendOperationLog.bind(this));
            window.addEventListener('click', this.sendOperationLog.bind(this));
            window.history.pushState = this.rewriteHis('pushState');
      
            window.history.replaceState = this.rewriteHis('replaceState');

            window.addEventListener('popstate', () => {
                this.sendPageEndLog();
            });

            window.addEventListener('pushState', () => {
                this.sendPageEndLog();
            });

            window.addEventListener('replaceState', () => {
                this.sendPageEndLog();
            });

            window.addEventListener('hashchange',()=>{
                this.sendPageEndLog();
            });

            window.addEventListener('beforeunload', () => {
                // 标识是否有操作
                this.log(SystemLids.endOperationLog, {_ho: this.hasOperation, _dp: parsePath(this._location)});
                // 离开时添加页面时长统计函数
                this.sendPageEndLog();
            });
        } else {
            console.warn('no window');
        }
        
        if (document) {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.sendEndLog();
                } else {
                    this.startTime = Date.now();
                }
            });
        }
    }
}
export default Hyperthymesia;

if (window) {
    window.Hyperthymesia = Hyperthymesia;
}